import { useCallback, useEffect, useMemo, useRef } from "react";

// @ts-expect-error - authuser.js は JS で型定義なし
import { useGetCurrentUserQuery } from "../accounts/authuser";
import {
  getAuthTokenFromCookies,
  removeAuthTokenFromCookies,
} from "../../common/utils";
import {
  createSpaSocket,
  type Envelope,
  type SpaSocket,
} from "../../utils/spaSocket";
import {
  resolveScopeKey,
  type MessengerIncomingEvent,
  type MessengerOutgoingEvent,
  type MessengerScope,
} from "../../types/messengerEvents";
import {
  MessengerSocketContext,
  type MessengerEventHandler,
  type MessengerReconnectHandler,
  type MessengerSocketContextValue,
} from "./MessengerSocketContext";

interface MessengerSocketProviderProps {
  children: React.ReactNode;
}

type SubscriberKey = string;

const makeSubscriberKey = (scope: MessengerScope, key: string): SubscriberKey =>
  `${scope}:${key}`;

/**
 * `/ws/spa/events` に接続し、メッセンジャー関連の受信イベントを
 * scope + primary id で購読できる EventBus として公開する Provider。
 *
 * - token / user.id が確定したら接続。
 * - 4401 受信でトークンを破棄してログイン画面に遷移。
 * - 2 回目以降の `connection.ready` 受信を再接続として扱い、
 *   購読側に再同期（REST 一覧再取得）を促す。
 */
const MessengerSocketProvider: React.FC<MessengerSocketProviderProps> = ({ children }) => {
  const { data: currentUserData } = useGetCurrentUserQuery();
  const userId: number | undefined = currentUserData?.data?.id;

  const socketRef = useRef<SpaSocket | null>(null);
  const subscribersRef = useRef<Map<SubscriberKey, Set<MessengerEventHandler>>>(
    new Map(),
  );
  const scopeSubscribersRef = useRef<
    Map<MessengerScope, Set<MessengerEventHandler>>
  >(new Map());
  const reconnectSubsRef = useRef<Set<MessengerReconnectHandler>>(new Set());
  const hasReadyOnceRef = useRef<boolean>(false);

  const sendEvent = useCallback((ev: MessengerOutgoingEvent): boolean => {
    if (!socketRef.current) return false;
    try {
      const payload = JSON.stringify(ev);
      return socketRef.current.send(payload);
    } catch (err) {
      console.warn("[messenger] failed to serialize outgoing event", err);
      return false;
    }
  }, []);

  const subscribe = useCallback<MessengerSocketContextValue["subscribe"]>(
    (scope, key, handler) => {
      const mapKey = makeSubscriberKey(scope, key);
      const bucket = subscribersRef.current.get(mapKey) ?? new Set();
      bucket.add(handler);
      subscribersRef.current.set(mapKey, bucket);
      return () => {
        const current = subscribersRef.current.get(mapKey);
        if (!current) return;
        current.delete(handler);
        if (current.size === 0) {
          subscribersRef.current.delete(mapKey);
        }
      };
    },
    [],
  );

  const subscribeScope = useCallback<MessengerSocketContextValue["subscribeScope"]>(
    (scope, handler) => {
      const bucket = scopeSubscribersRef.current.get(scope) ?? new Set();
      bucket.add(handler);
      scopeSubscribersRef.current.set(scope, bucket);
      return () => {
        const current = scopeSubscribersRef.current.get(scope);
        if (!current) return;
        current.delete(handler);
        if (current.size === 0) {
          scopeSubscribersRef.current.delete(scope);
        }
      };
    },
    [],
  );

  const subscribeReconnect = useCallback<
    MessengerSocketContextValue["subscribeReconnect"]
  >((handler) => {
    reconnectSubsRef.current.add(handler);
    return () => {
      reconnectSubsRef.current.delete(handler);
    };
  }, []);

  const contextValue = useMemo<MessengerSocketContextValue>(
    () => ({ sendEvent, subscribe, subscribeScope, subscribeReconnect }),
    [sendEvent, subscribe, subscribeScope, subscribeReconnect],
  );

  useEffect(() => {
    const token = getAuthTokenFromCookies();
    if (!token || !userId) {
      return;
    }

    const handleUnauthorized = () => {
      removeAuthTokenFromCookies();
      window.location.replace("/signin");
    };

    const handleMessage = (envelope: Envelope) => {
      const ev = envelope as MessengerIncomingEvent;

      // connection.ready は再接続検知に利用する。
      if (ev.type === "connection.ready") {
        if (hasReadyOnceRef.current) {
          // 2 回目以降 = 再接続完了。切断中の取りこぼしがある可能性があるので通知。
          reconnectSubsRef.current.forEach((handler) => {
            try {
              handler();
            } catch (err) {
              console.warn("[messenger] reconnect handler error", err);
            }
          });
        } else {
          hasReadyOnceRef.current = true;
        }
        return;
      }

      if (ev.type === "pong") return;

      const resolved = resolveScopeKey(ev);
      if (!resolved) {
        // 未知 type または primary id 欠落。サイレント無視（前方互換）。
        return;
      }

      const dispatchToHandlers = (
        handlers: Set<MessengerEventHandler> | undefined,
      ) => {
        if (!handlers || handlers.size === 0) return;
        handlers.forEach((handler) => {
          try {
            handler(ev);
          } catch (err) {
            console.warn("[messenger] subscriber handler error", err);
          }
        });
      };

      // スコープ全体購読を key 単位購読より先に配信（キャッシュ読み取り順を保証）。
      dispatchToHandlers(scopeSubscribersRef.current.get(resolved.scope));
      dispatchToHandlers(
        subscribersRef.current.get(makeSubscriberKey(resolved.scope, resolved.key)),
      );
    };

    hasReadyOnceRef.current = false;
    socketRef.current = createSpaSocket({
      path: "events",
      token,
      onMessage: handleMessage,
      onUnauthorized: handleUnauthorized,
      // タブがバックグラウンドに落ちた間にネットワーク瞬断や OS 側のソケット切断が
      // 起きていた場合、表面上は OPEN のまま実体が切れている状態になり得る。
      // visible 復帰で一度切って張り直し、`connection.ready` 再受信で
      // 購読側の一覧再取得をトリガさせる（reconnect handler 経由）。
      reconnectOnVisible: true,
    });

    return () => {
      socketRef.current?.disconnect();
      socketRef.current = null;
      hasReadyOnceRef.current = false;
    };
  }, [userId]);

  return (
    <MessengerSocketContext.Provider value={contextValue}>
      {children}
    </MessengerSocketContext.Provider>
  );
};

export default MessengerSocketProvider;
